import { get } from 'lodash';

const DATA_SOURCE_TYPES = {
    HOME: 'home',
    MY_LIST: 'my_list',
    CONTACT_FORM: 'contact_form',
    SMART_SEARCH: 'smart_search',
    DROP_DOWN: 'drop_down',
    ARTIST: 'artist',
    CONTENT_CREATOR_TALENT: 'content_creator_talent',
    CONTENT_CREATOR_AUDIOVISUAL: 'content_creator_audiovisual',
    CONTENT_CREATOR_EDITORIAL: 'content_creator_editorial',
    VIDEO_CONTENT_CREATION: 'video_content_creation',
    CHANNEL_PLAYLIST: 'channel_playlist',
    USER_LIBRARY: 'user_library',
    CATALOG: 'catalog',
    RECOMMENDED: 'recommended',
    PRODUCTION: 'production',
    SECTION: 'section',
    SINGLE_CONTENT_CREATOR: 'single_content_creator',
    LINK: 'link',
    CUSTOM: 'custom',
    CUSTOM_EMBED: 'custom_embed',
    TEXT: 'text',
    VIDEO: 'video',
    IMAGE: 'image',
    GOOGLE_AD: 'google_ad',
    CHANGE_LANGUAGE: 'change_language',
    EMBED: 'embed',
    LITE_SMART_SEARCH: 'lite_smart_search',
    PLAYLIST_LINK: 'playlist_link',
    STATIC: 'static'
  };
  
  // Group data sources by category for easier filtering
  const DATA_SOURCE_GROUPS = {
    CONTENT_CREATOR: [
      DATA_SOURCE_TYPES.CONTENT_CREATOR_TALENT,
      DATA_SOURCE_TYPES.CONTENT_CREATOR_AUDIOVISUAL,
      DATA_SOURCE_TYPES.CONTENT_CREATOR_EDITORIAL,
      DATA_SOURCE_TYPES.VIDEO_CONTENT_CREATION,
      DATA_SOURCE_TYPES.SINGLE_CONTENT_CREATOR
    ],
    MUSIC: [
      DATA_SOURCE_TYPES.CATALOG,
      DATA_SOURCE_TYPES.ARTIST
    ],
    CREATING_ELEMENTS: [
      DATA_SOURCE_TYPES.LINK,
      DATA_SOURCE_TYPES.MY_LIST,
      DATA_SOURCE_TYPES.PLAYLIST_LINK,
      DATA_SOURCE_TYPES.SMART_SEARCH,
      DATA_SOURCE_TYPES.CONTACT_FORM,
      DATA_SOURCE_TYPES.HOME,
      DATA_SOURCE_TYPES.DROP_DOWN,
      DATA_SOURCE_TYPES.SINGLE_CONTENT_CREATOR,
      DATA_SOURCE_TYPES.SECTION,
      DATA_SOURCE_TYPES.CHANGE_LANGUAGE
    ],
    UNIQUE_ELEMENTS: [
      DATA_SOURCE_TYPES.TEXT,
      DATA_SOURCE_TYPES.VIDEO,
      DATA_SOURCE_TYPES.IMAGE,
      DATA_SOURCE_TYPES.GOOGLE_AD,
      DATA_SOURCE_TYPES.VIDEO_CONTENT_CREATION,
      DATA_SOURCE_TYPES.LITE_SMART_SEARCH
    ],
    CUSTOM_ELEMENTS: [
      DATA_SOURCE_TYPES.PRODUCTION,
      DATA_SOURCE_TYPES.USER_LIBRARY,
      DATA_SOURCE_TYPES.MY_LIST,
      DATA_SOURCE_TYPES.PLAYLIST_LINK,
      DATA_SOURCE_TYPES.SMART_SEARCH,
      DATA_SOURCE_TYPES.CONTACT_FORM,
      DATA_SOURCE_TYPES.HOME,
      DATA_SOURCE_TYPES.DROP_DOWN,
      DATA_SOURCE_TYPES.RECOMMENDED,
      DATA_SOURCE_TYPES.VIDEO_CONTENT_CREATION,
      DATA_SOURCE_TYPES.ARTIST
    ],
    REDIRECT_BUTTON_EXCLUDED: [
      DATA_SOURCE_TYPES.PRODUCTION,
      DATA_SOURCE_TYPES.USER_LIBRARY,
      DATA_SOURCE_TYPES.MY_LIST,
      DATA_SOURCE_TYPES.PLAYLIST_LINK,
      DATA_SOURCE_TYPES.SMART_SEARCH,
      DATA_SOURCE_TYPES.CONTACT_FORM,
      DATA_SOURCE_TYPES.HOME,
      DATA_SOURCE_TYPES.DROP_DOWN,
      DATA_SOURCE_TYPES.RECOMMENDED,
      DATA_SOURCE_TYPES.LINK,
      DATA_SOURCE_TYPES.CHANGE_LANGUAGE,
      DATA_SOURCE_TYPES.EMBED,
      DATA_SOURCE_TYPES.SINGLE_CONTENT_CREATOR,
      DATA_SOURCE_TYPES.SECTION,
      DATA_SOURCE_TYPES.CHANNEL_PLAYLIST,
      DATA_SOURCE_TYPES.TEXT,
      DATA_SOURCE_TYPES.VIDEO,
      DATA_SOURCE_TYPES.IMAGE,
      DATA_SOURCE_TYPES.GOOGLE_AD,
      DATA_SOURCE_TYPES.VIDEO_CONTENT_CREATION,
      DATA_SOURCE_TYPES.LITE_SMART_SEARCH,
      DATA_SOURCE_TYPES.ARTIST,
      DATA_SOURCE_TYPES.STATIC
    ]
  };
  
  const filterDataSources = (dataSources, excludedValues) => {
    return dataSources.filter(dataSource => !excludedValues.includes(dataSource.value));
  };
  
  const hasPermission = (permissions, entity) => {
    return permissions.length === 0 || permissions.find(permission => permission.entity === entity);
  };
  
  export const checkDataSourcesAgainstPermissions = (permissions, dataSourceTypes, channel = null, channelId = null, parent = null, custom = false, unique = false) => {
    let filteredDataSources = [...dataSourceTypes];
    
    const context = {
      creatingElements: !!get(channel, 'creatingElements'),
      isRedirectButton: !!get(channel, 'redirectButton'),
      isEmbed: !!get(channel, 'isEmbed'),
      hasChannel: !!channel,
      hasParent: !!get(parent, 'id'),
      isContentChannel: get(channel, 'type') === "content",
      isSubNavbarOrFooter: get(parent, 'data_source') === "sub_navbar" || get(parent, 'type') === "footer"
    };
  
    // Filter by content creator permissions
    if (!hasPermission(permissions, 'content_creator')) {
      filteredDataSources = filterDataSources(filteredDataSources, DATA_SOURCE_GROUPS.CONTENT_CREATOR);
    }
  
    // Filter by music permissions
    if (!hasPermission(permissions, 'music')) {
      filteredDataSources = filterDataSources(filteredDataSources, DATA_SOURCE_GROUPS.MUSIC);
    }
  
    // Filter by pitch permissions or channel context
    if (!hasPermission(permissions, 'pitch') || context.hasChannel) {
      filteredDataSources = filterDataSources(filteredDataSources, [DATA_SOURCE_TYPES.PRODUCTION]);
    }
  
    // Filter by channel context
    if (context.hasChannel) {
      filteredDataSources = filterDataSources(filteredDataSources, [DATA_SOURCE_TYPES.USER_LIBRARY]);
      
      if (context.isContentChannel) {
        filteredDataSources = filterDataSources(filteredDataSources, DATA_SOURCE_GROUPS.MUSIC);
        filteredDataSources = filterDataSources(filteredDataSources, [DATA_SOURCE_TYPES.RECOMMENDED]);
      }
    } else {
      // When no channel, filter out channel-specific data sources
      if (!channelId) {
        const channelSpecificSources = [
          DATA_SOURCE_TYPES.CHANNEL_PLAYLIST,
          DATA_SOURCE_TYPES.CUSTOM,
          DATA_SOURCE_TYPES.CUSTOM_EMBED
        ];
        filteredDataSources = filterDataSources(filteredDataSources, channelSpecificSources);
      }
    }
  
    // Filter by parent context
    if (context.hasParent) {
      const parentExcludedSources = [
        DATA_SOURCE_TYPES.VIDEO_CONTENT_CREATION,
        DATA_SOURCE_TYPES.MY_LIST,
        DATA_SOURCE_TYPES.PLAYLIST_LINK,
        DATA_SOURCE_TYPES.HOME,
        DATA_SOURCE_TYPES.CUSTOM,
        DATA_SOURCE_TYPES.CUSTOM_EMBED
      ];
      filteredDataSources = filterDataSources(filteredDataSources, parentExcludedSources);
      
      if (context.isSubNavbarOrFooter) {
        filteredDataSources = filterDataSources(filteredDataSources, [DATA_SOURCE_TYPES.DROP_DOWN]);
      }
    }
  
    // Filter by creating elements context
    if (!context.creatingElements) {
      filteredDataSources = filterDataSources(filteredDataSources, DATA_SOURCE_GROUPS.CREATING_ELEMENTS);
    } else if (unique) {
      filteredDataSources = filterDataSources(filteredDataSources, DATA_SOURCE_GROUPS.UNIQUE_ELEMENTS);
    } else {
      const creatingElementsExcluded = [
        DATA_SOURCE_TYPES.VIDEO_CONTENT_CREATION,
        DATA_SOURCE_TYPES.CUSTOM,
        DATA_SOURCE_TYPES.CUSTOM_EMBED,
        DATA_SOURCE_TYPES.RECOMMENDED,
        DATA_SOURCE_TYPES.ARTIST
      ];
      filteredDataSources = filterDataSources(filteredDataSources, creatingElementsExcluded);
    }
  
    // Filter by custom context
    if (custom) {
      filteredDataSources = filterDataSources(filteredDataSources, DATA_SOURCE_GROUPS.CUSTOM_ELEMENTS);
    } else {
      filteredDataSources = filterDataSources(filteredDataSources, [DATA_SOURCE_TYPES.STATIC]);
    }
  
    // Filter by redirect button context
    if (context.isRedirectButton) {
      filteredDataSources = filterDataSources(filteredDataSources, DATA_SOURCE_GROUPS.REDIRECT_BUTTON_EXCLUDED);
    }
  
    // Filter by embed context
    if (context.isEmbed) {
      filteredDataSources = filteredDataSources.filter(dataSource => dataSource.value === DATA_SOURCE_TYPES.EMBED);
    } else {
      filteredDataSources = filterDataSources(filteredDataSources, [DATA_SOURCE_TYPES.EMBED]);
    }
  
    return filteredDataSources;
  };
  